import { cn } from '@/lib/cn';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';

/**
 * Zakladki z podkresleniem. Na telefonie JEDEN rzad przewijany w bok
 * (zamiast zawijania na 4–5 rzedow — audyt PR-004); aktywna zakladka
 * automatycznie przewija sie do widoku.
 */
export interface TabItem<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  count?: number;
  disabled?: boolean;
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
  className,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  items: ReadonlyArray<TabItem<T>>;
  className?: string;
  ariaLabel?: string;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: przewijamy przy zmianie zakladki
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        '-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 [scrollbar-width:none] sm:-mx-6 sm:px-6 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={active ? activeRef : undefined}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={tab.disabled}
            onClick={() => onChange(tab.value)}
            className={cn(
              'relative -mb-px inline-flex min-h-touch shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors md:min-h-10',
              active
                ? 'border-accent text-fg'
                : 'border-transparent text-fg-muted hover:border-border-strong hover:text-fg',
              tab.disabled && 'cursor-not-allowed opacity-40 hover:border-transparent',
            )}
          >
            {tab.icon && (
              <tab.icon className={cn('h-4 w-4', active ? 'text-accent' : '')} strokeWidth={2} />
            )}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'rounded-md px-1.5 text-xs leading-5',
                  active ? 'bg-accent-subtle text-accent' : 'bg-surface-2 text-fg-muted',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
